import { useState, useEffect } from "react";
import { PageLayout } from "@/components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { 
  Shield, 
  Users, 
  Target, 
  Award, 
  Shuffle, 
  CheckCircle, 
  Trophy, 
  BarChart3, 
  Calendar, 
  DollarSign,
  LayoutDashboard,
  Save,
  AlertTriangle,
  User,
  Edit3,
  Key,
  UserPlus,
  History
} from "lucide-react";

// Funcionalidades para Avaliadores e Técnicos
const EVALUATOR_FEATURES = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, description: "Painel principal com estatísticas" },
  { id: "evaluate", label: "Avaliar Jogadores", icon: Target, description: "Realizar avaliações de jogadores" },
  { id: "players", label: "Jogadores", icon: Users, description: "Visualizar e editar jogadores" },
  { id: "ranking", label: "Ranking", icon: Award, description: "Ver ranking e comparar jogadores" },
  { id: "statistics", label: "Estatísticas", icon: BarChart3, description: "Visualizar estatísticas gerais" },
  { id: "evaluations_panel", label: "Painel de Avaliações", icon: BarChart3, description: "Ver todas as avaliações" },
  { id: "edit_evaluations", label: "Editar/Excluir Avaliações", icon: Edit3, description: "Modificar ou remover avaliações", adminOnly: true },
  { id: "calendar", label: "Calendário", icon: Calendar, description: "Ver calendário de jogos" },
  { id: "attendance", label: "Frequência", icon: CheckCircle, description: "Ver frequência dos jogadores" },
  { id: "gameday", label: "Times do Dia", icon: Shuffle, description: "Gerar e gerenciar times do dia" },
  { id: "game-registrations", label: "Inscrições", icon: CheckCircle, description: "Gerenciar inscrições em jogos" },
  { id: "teams", label: "Times", icon: Trophy, description: "Gerenciar times salvos" },
  { id: "evaluators", label: "Avaliadores", icon: Shield, description: "Gerenciar avaliadores" },
  { id: "payments", label: "Pagamentos", icon: DollarSign, description: "Gerenciar pagamentos" },
];

// Funcionalidades para Jogadores
const PLAYER_FEATURES = [
  { id: "view_profile", label: "Ver Perfil", icon: User, description: "Visualizar informações do perfil" },
  { id: "edit_profile", label: "Editar Perfil", icon: Edit3, description: "Editar nome, posição, email, telefone" },
  { id: "change_password", label: "Trocar Senha", icon: Key, description: "Alterar a senha de acesso" },
  { id: "change_photo", label: "Trocar Foto", icon: User, description: "Alterar foto do perfil" },
  { id: "view_calendar", label: "Ver Calendário", icon: Calendar, description: "Ver calendário de jogos" },
  { id: "view_games", label: "Ver Jogos", icon: Trophy, description: "Ver histórico de jogos" },
  { id: "view_teammates", label: "Ver Companheiros", icon: Users, description: "Ver estatísticas de companheiros" },
  { id: "register_games", label: "Inscrever em Jogos", icon: UserPlus, description: "Se inscrever em jogos abertos" },
  { id: "view_history", label: "Ver Histórico", icon: History, description: "Ver histórico de avaliações" },
];

export default function RolePermissionsPage({ evaluator, onLogout }) {
  const { t } = useLanguage();
  const [permissions, setPermissions] = useState({
    avaliador: {},
    tecnico: {},
    jogador: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("staff"); // "staff" or "players"

  useEffect(() => {
    fetchPermissions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/role-permissions`);
      setPermissions(prev => ({
        ...prev,
        ...response.data
      }));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleToggle = (role, featureId) => {
    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [featureId]: !prev[role][featureId]
      }
    }));
    setHasChanges(true);
  };

  const saveAllPermissions = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/role-permissions/avaliador`, {
          role: "avaliador",
          features: permissions.avaliador
        }),
        axios.put(`${API}/role-permissions/tecnico`, {
          role: "tecnico",
          features: permissions.tecnico
        }),
        axios.put(`${API}/role-permissions/jogador`, {
          role: "jogador",
          features: permissions.jogador
        })
      ]);
      setHasChanges(false);
      toast.success("Permissões salvas com sucesso! Os usuários verão as mudanças ao recarregar a página.");
    } catch (error) {
      toast.error("Erro ao salvar permissões");
    }
    setSaving(false);
  };

  const renderFeatureList = (role, features) => {
    return (
      <div className="divide-y divide-border">
        {features.map((feature) => {
          const Icon = feature.icon;
          const isEnabled = permissions[role]?.[feature.id] ?? (role === 'tecnico' ? true : role === 'jogador' ? true : false);
          return (
            <div 
              key={feature.id}
              className={`flex items-center justify-between p-4 transition-colors ${
                isEnabled ? 'bg-green-500/5' : 'bg-red-500/5'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isEnabled ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  <Icon className={`h-5 w-5 ${isEnabled ? 'text-green-500' : 'text-red-500'}`} />
                </div>
                <div>
                  <p className={`font-medium ${isEnabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {feature.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                </div>
              </div>
              <Switch
                checked={isEnabled}
                onCheckedChange={() => handleToggle(role, feature.id)}
                className={isEnabled ? 'data-[state=checked]:bg-green-500' : 'data-[state=unchecked]:bg-red-500'}
              />
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <PageLayout evaluator={evaluator} onLogout={onLogout} title="Permissões">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout evaluator={evaluator} onLogout={onLogout} title={t('permissionsAdmin.title')}>
      {/* Header Alert */}
      {hasChanges && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span className="text-foreground font-medium">Existem alterações não salvas</span>
          </div>
          <Button 
            onClick={saveAllPermissions}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Salvando..." : t('permissionsAdmin.save')}
          </Button>
        </div>
      )}

      {/* Tab Buttons */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === "staff" ? "default" : "outline"}
          onClick={() => setActiveTab("staff")}
          className={activeTab === "staff" ? "bg-red-600 hover:bg-red-700" : ""}
        >
          <Shield className="h-4 w-4 mr-2" />
          Avaliadores & Técnicos
        </Button>
        <Button
          variant={activeTab === "players" ? "default" : "outline"}
          onClick={() => setActiveTab("players")}
          className={activeTab === "players" ? "bg-green-600 hover:bg-green-700" : ""}
        >
          <Users className="h-4 w-4 mr-2" />
          Jogadores
        </Button>
      </div>

      {/* Staff Permissions (Avaliadores & Técnicos) */}
      {activeTab === "staff" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Avaliador Permissions */}
          <Card className="bg-card/80 backdrop-blur-md border-2 border-red-500/30 rounded-xl shadow-2xl">
            <CardHeader className="border-b border-red-500/20 pb-4 bg-red-500/5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold uppercase tracking-tight text-foreground flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-red-500" />
                  </div>
                  Avaliador
                </CardTitle>
                <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
                  {Object.values(permissions.avaliador || {}).filter(Boolean).length} / {EVALUATOR_FEATURES.length} ativas
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Configure quais funcionalidades os avaliadores podem acessar
              </p>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {renderFeatureList('avaliador', EVALUATOR_FEATURES)}
            </CardContent>
          </Card>

          {/* Técnico Permissions */}
          <Card className="bg-card/80 backdrop-blur-md border-2 border-red-500/30 rounded-xl shadow-2xl">
            <CardHeader className="border-b border-red-500/20 pb-4 bg-red-500/5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold uppercase tracking-tight text-foreground flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-red-500" />
                  </div>
                  Técnico
                </CardTitle>
                <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
                  {Object.values(permissions.tecnico || {}).filter(Boolean).length} / {EVALUATOR_FEATURES.length} ativas
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Configure quais funcionalidades os técnicos podem acessar
              </p>
            </CardHeader>
            <CardContent className="p-0 max-h-[500px] overflow-y-auto">
              {renderFeatureList('tecnico', EVALUATOR_FEATURES)}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Player Permissions */}
      {activeTab === "players" && (
        <Card className="bg-card/80 backdrop-blur-md border-2 border-green-500/30 rounded-xl shadow-2xl max-w-2xl mx-auto">
          <CardHeader className="border-b border-green-500/20 pb-4 bg-green-500/5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-bold uppercase tracking-tight text-foreground flex items-center gap-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <User className="h-5 w-5 text-green-500" />
                </div>
                Jogadores
              </CardTitle>
              <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                {Object.values(permissions.jogador || {}).filter(Boolean).length} / {PLAYER_FEATURES.length} ativas
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Configure quais funcionalidades os jogadores podem acessar em seu painel
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {renderFeatureList('jogador', PLAYER_FEATURES)}
          </CardContent>
        </Card>
      )}

      {/* Save All Button */}
      <div className="mt-6 flex justify-center">
        <Button 
          onClick={saveAllPermissions}
          disabled={saving || !hasChanges}
          size="lg"
          className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(239,68,68,0.3)]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? "Salvando..." : t('permissionsAdmin.save')}
        </Button>
      </div>
    </PageLayout>
  );
}
